import type { RouteResponse, Token } from "@0xsquid/squid-types";
import type {
  EventListenerFunction,
  SwapEventPayload,
  WidgetEventMap,
} from "../../core/types/event";
import { ExecuteQuoteClient } from "../../core/types/onramps/onramp";
import { RouteRequest } from "../../core/types/route";
import { formatUnitsRounded } from "./numberService";

type FindTokenMethod = (
  address?: string,
  chainId?: string
) => Token | undefined;

/**
 * Creates a hash string from quote request parameters to track changes
 */
export const createQuoteRequestParamsHash = (params: RouteRequest): string => {
  return [
    params.fromAddress || "",
    params.toAddress || "",
    params.fromChain,
    params.toChain,
    `${params.fromToken}_${params.fromChain}`,
    `${params.toToken}_${params.toChain}`,
    params.fromAmount,
  ].join("|");
};

/**
 * Formats the amount based on token details
 * @param data Event parameters containing fromToken, fromChain, and fromAmount
 * @param findToken Function to find token details
 * @returns Original data with formatted amount
 */
const formatTokenAmountForEvent = <
  T extends { fromToken?: string; fromChain?: string; fromAmount?: string },
>(
  data: T,
  findToken: FindTokenMethod
): T => {
  const fromToken = findToken(data.fromToken, data.fromChain);

  const formattedFromAmount = formatUnitsRounded(
    (data.fromAmount ?? "0").toString(),
    fromToken?.decimals,
    10
  );

  // Rewrite the fromAmount with the formatted amount
  // Example: 1000000000000000000 -> 1 with decimals 18
  // For readability of the event data
  return {
    ...data,
    fromAmount: formattedFromAmount,
  };
};

export class WidgetEvents extends EventTarget {
  private static instance: WidgetEvents;

  private constructor() {
    super();
  }

  private getRouteEventPayload = (
    route: RouteResponse["route"],
    txHash: string
  ): SwapEventPayload => {
    const {
      fromAddress,
      toAddress,
      fromChain,
      toChain,
      fromToken,
      toToken,
      fromAmount,
    } = route.params;
    return {
      fromAddress,
      toAddress,
      fromChain,
      toChain,
      fromToken,
      toToken,
      fromAmount,
      quoteId: route.quoteId,
      txHash,
    };
  };

  // Singleton, because we need to access this service outside the widget
  public static getInstance(): WidgetEvents {
    if (!WidgetEvents.instance) {
      WidgetEvents.instance = new WidgetEvents();
    }
    return WidgetEvents.instance;
  }

  /**
   * Overriding the addEventListener method to make it easier to use with typescript
   * @param type
   * @param listener
   * @param options
   */
  listenToWidget<T extends keyof WidgetEventMap>(
    type: T,
    listener: EventListenerFunction<T>,
    options?: boolean | AddEventListenerOptions
  ) {
    super.addEventListener(type, listener as unknown as EventListener, options);
  }

  /**
   * Overriding the removeEventListener method to make it easier to use with typescript
   * @param type
   * @param listener
   * @param options
   */
  removeWidgetListener<T extends keyof WidgetEventMap>(
    type: T,
    listener: EventListenerFunction<T>,
    options?: boolean | EventListenerOptions
  ) {
    super.removeEventListener(
      type,
      listener as unknown as EventListener,
      options
    );
  }

  /**
   * Dispatch an event of the widget
   * To be listened from an integrator website
   * For example to display a success message when transaction is done
   * @param name
   * @param data
   */
  dispatch<T extends keyof WidgetEventMap>(name: T, data: WidgetEventMap[T]) {
    this.dispatchEvent(new CustomEvent(name, { detail: data }));
  }

  /**
   * Will dispatch the main axelar status of transaction
   * This will be called every time the status is received by backend
   * (Using interval to check status)
   * @param status
   */
  dispatchSwapStatus(status: string) {
    this.dispatch("swapStatus", { status });
  }

  /**
   * Dispatch event when user executes a swap
   * Only when we have the tx hash received
   * @param route
   */
  dispatchSwapExecuteCall(route: RouteResponse["route"], txHash: string) {
    this.dispatch("swap", this.getRouteEventPayload(route, txHash));
  }

  /**
   * Dispatch event when user changes chain/token
   * @param swapParams
   */
  dispatchSwapParamsChanged(swapParams: {
    fromChainId?: string;
    toChainId?: string;
    fromTokenAddress?: string;
    toTokenAddress?: string;
    fromNetworkName?: string;
    toNetworkName?: string;
    fromTokenSymbol?: string;
    toTokenSymbol?: string;
  }) {
    this.dispatch("swapParamsChanged", { ...swapParams });
  }

  /**
   * Dispatch event when we are waiting for the user to sign the swap transaction
   * @param data
   */
  dispatchSwapTxSignatureRequested(
    data: WidgetEventMap["swapTxSignatureRequested"]
  ) {
    this.dispatch("swapTxSignatureRequested", data);
  }

  /**
   * Dispatch event when there's an error while executing the swap route
   * @param data
   */
  dispatchSwapRouteExecutionError(
    data: WidgetEventMap["swapRouteExecutionError"]
  ) {
    this.dispatch("swapRouteExecutionError", data);
  }

  /**
   * Dispatch event when requesting a quote for swap
   * @param data Quote request parameters
   */
  dispatchRequestQuote(data: WidgetEventMap["requestQuote"]) {
    this.dispatch("requestQuote", data);
  }

  /**
   * Dispatch event before swap execution
   * @param data Pre-swap parameters
   * @param findToken Function to find token details
   */
  dispatchPreSwap(data: WidgetEventMap["preSwap"], findToken: FindTokenMethod) {
    this.dispatch("preSwap", formatTokenAmountForEvent(data, findToken));
  }

  /**
   * Dispatch event after swap execution
   * @param data Post-swap parameters including transaction hash
   * @param findToken Function to find token details
   */
  dispatchPostSwap(
    data: WidgetEventMap["postSwap"],
    findToken: FindTokenMethod
  ) {
    this.dispatch("postSwap", formatTokenAmountForEvent(data, findToken));
  }

  /**
   * Dispatch event when wallet is successfully connected
   * @param data Wallet connection details
   */
  dispatchWalletConnect(data: WidgetEventMap["walletConnect"]) {
    this.dispatch("walletConnect", data);
  }

  /**
   * Dispatch event when wallet connection is rejected
   * @param data Wallet rejection details
   */
  dispatchWalletReject(data: WidgetEventMap["walletReject"]) {
    this.dispatch("walletReject", data);
  }

  /**
   * Dispatch event when onchain transaction is rejected
   * @param data Onchain rejection details
   */
  dispatchOnchainReject(data: WidgetEventMap["onchainReject"]) {
    this.dispatch("onchainReject", data);
  }

  /**
   * Dispatch event when requesting an onramp quote
   * @param data Quote request parameters
   */
  dispatchOnrampQuoteRequest(data: {
    fiatCurrency: string;
    cryptoCurrencyID: string;
    amount: number;
    region?: string;
    paymentMethod?: string;
  }) {
    this.dispatch("onrampQuoteRequest", data);
  }

  /**
   * Dispatch event when executing an onramp quote
   * @param data Quote execution parameters
   */
  dispatchOnrampQuoteExecute(data: ExecuteQuoteClient) {
    this.dispatch("onrampQuoteExecute", {
      tokenAddress: data.squidToken?.address,
      tokenChainId: data.squidToken?.chainId,
      amount: data.cryptoAmount,
      paymentMethod: data.paymentMethod,
      walletAddress: data.walletAddress,
      orderId: data.orderId,
      fiatCurrency: data.fiatCurrency,
      cryptoCurrencyID: data.cryptoCurrencyID,
      region: data.region,
      onrampProviderId: data.onrampProviderId,
    });
  }

  /**
   * Dispatch event when the search query has no results
   * @param data Empty search details
   */
  dispatchSearchEmpty(data: WidgetEventMap["searchEmpty"]) {
    this.dispatch("searchEmpty", data);
  }

  /**
   * Dispatch event before Send transaction execution
   * @param data Pre-send parameters
   */
  dispatchPreSend(data: WidgetEventMap["preSend"]) {
    this.dispatch("preSend", data);
  }

  /**
   * Dispatch event after Send transaction execution
   * @param data Post-send parameters
   */
  dispatchPostSend(data: WidgetEventMap["postSend"]) {
    this.dispatch("postSend", data);
  }

  /**
   * Dispatch event when QR code is generated for wallet connection.
   * @param data QR code data
   */
  dispatchQrCodeGeneratedForConnect(
    data: WidgetEventMap["qrCodeGeneratedForConnect"]
  ) {
    this.dispatch("qrCodeGeneratedForConnect", data);
  }

  /**
   * Dispatch event when QR code is generated for signing a transaction.
   * @param data QR code data
   */
  dispatchQrCodeGeneratedForSign(
    data: WidgetEventMap["qrCodeGeneratedForSign"]
  ) {
    this.dispatch("qrCodeGeneratedForSign", data);
  }

  /**
   * Dispatch event when QR code generation for signing a transaction fails.
   * @param data Error information
   */
  dispatchQrCodeGenerationFailedForSign(
    data: WidgetEventMap["qrCodeGenerationFailedForSign"]
  ) {
    this.dispatch("qrCodeGenerationFailedForSign", data);
  }

  /**
   * Dispatches event after the QR code for signing a transaction is scanned
   * and we're waiting for the user to approve or reject it.
   */
  dispatchQrTxScannedAwaitingApproval(
    data: WidgetEventMap["qrTxScannedAwaitingUserApproval"]
  ) {
    this.dispatch("qrTxScannedAwaitingUserApproval", data);
  }
}
